import { CHECK_IN_VISIT_TYPES, VISIT_STATUSES, VISIT_TYPES } from '@wodi/domain';
import { z } from 'zod';

import { reasonSchema } from './clinical-common.ts';

// Visits (encounters), the spine of every clinical and financial record (CLAUDE.md,
// domain rule 1), and the first queue entry made at check-in
// (docs/plans/phase1-registration.md §2.12–2.14, §3.3).

export { CHECK_IN_VISIT_TYPES, VISIT_STATUSES, VISIT_TYPES };

/**
 * All payer kinds exist so the table keeps its shape; this slice accepts only `cash`.
 * `other` covers NGO and county payers.
 */
export const PAYER_KINDS = ['cash', 'sha', 'insurer', 'corporate', 'other'] as const;
export type PayerKind = (typeof PAYER_KINDS)[number];

export const QUEUE_STATUSES = [
  'waiting',
  'called',
  'in_service',
  'done',
  'transferred_out',
  'cancelled',
] as const;
export type QueueStatus = (typeof QUEUE_STATUSES)[number];

export const openVisitRequestSchema = z
  .object({
    /** Generated once per check-in; a retried request returns the same visit. */
    clientRequestId: z.uuid(),
    patientId: z.uuid(),
    /** Inpatient visits are opened by admission, never at check-in. */
    type: z.enum(CHECK_IN_VISIT_TYPES),
    /**
     * Cash, or SHA with the eligibility check and covered scheme the visit relies on
     * (docs/plans/phase1-sha.md §7.1). Other payers are refused with `payer_not_available`
     * until their slices land.
     */
    payer: z.union([
      z.object({
        kind: z.literal('sha'),
        eligibilityCheckId: z.uuid(),
        schemeId: z.uuid(),
      }),
      // A private insurer, through a cover recorded for the patient (private insurance plan §1.1).
      z.object({ kind: z.literal('insurer'), coverageId: z.uuid() }),
      z.object({ kind: z.enum(['cash', 'corporate', 'other']) }),
    ]),
    firstServicePointId: z.uuid(),
    /**
     * Backdated arrival, earlier the same East Africa day (plan §12, decision 13). Absent
     * means now. Needs `arrivalReason`, which is stored with the visit and audited.
     */
    arrivedAt: z.iso.datetime().optional(),
    arrivalReason: reasonSchema.optional(),
    /**
     * Checking in for a booked appointment (docs/plans/phase2-appointments.md §3.2): it must
     * be the same patient's, at this facility, today. It is marked arrived with the visit.
     */
    appointmentId: z.uuid().optional(),
  })
  .refine((v) => (v.arrivedAt === undefined) === (v.arrivalReason === undefined), {
    message: 'A backdated arrival time needs a reason',
    path: ['arrivalReason'],
  });

export type OpenVisitRequest = z.infer<typeof openVisitRequestSchema>;

export const visitPayerSchema = z.object({
  rank: z.union([z.literal(1), z.literal(2)]),
  kind: z.enum(PAYER_KINDS),
  status: z.enum(['active', 'replaced']),
});

export const queueEntrySchema = z.object({
  id: z.uuid(),
  servicePoint: z.object({ id: z.uuid(), name: z.string() }),
  status: z.enum(QUEUE_STATUSES),
  enteredAt: z.iso.datetime(),
});

export const visitSummarySchema = z.object({
  id: z.uuid(),
  /** Shown in the mono face; never in a URL. */
  visitNumber: z.string(),
  /** The daily queue ticket, e.g. "042"; null for visits opened before tickets existed. */
  queueTicket: z.string().nullable(),
  patientId: z.uuid(),
  facility: z.object({ id: z.uuid(), name: z.string() }),
  type: z.enum(VISIT_TYPES),
  status: z.enum(VISIT_STATUSES),
  /** Arrival: the clinical event time, separate from when it was entered (domain rule 5). */
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().nullable(),
});

export type VisitSummary = z.infer<typeof visitSummarySchema>;

export const visitSchema = visitSummarySchema.extend({
  /** Why arrival was backdated, when it was. */
  startedAtReason: z.string().nullable(),
  /** Why the visit was cancelled, when it was. */
  statusReason: z.string().nullable(),
  payers: z.array(visitPayerSchema),
  /** The visit's current place in a queue, if it has one. */
  queueEntry: queueEntrySchema.nullable(),
  createdAt: z.iso.datetime(),
});

export type VisitView = z.infer<typeof visitSchema>;

export const cancelVisitRequestSchema = z.object({ reason: reasonSchema });
